import { assertNever } from "@impero-com/common/utils/obj";
import { CONTROL_ACTIVITIES_DATA_PARAM } from "@impero-com/ui/domain";

import { ArchivedStatus } from "api/generated/model";

import { ControlActivitiesDataParamsApi } from "./useControlActivitiesDataParams";

type ArchivedSelection = "both" | "activeOnly" | "archivedOnly" | "none";

export interface ControlActivitiesArchivedFilters {
    active: boolean;
    archived: boolean;
    setActive: (checked: boolean) => void;
    setArchived: (checked: boolean) => void;
}

export interface ControlActivitiesArchivedFilterResult extends ControlActivitiesArchivedFilters {
    archivedStatus: ArchivedStatus | undefined;
    /** False when neither box is checked; the query should not run. */
    isEnabled: boolean;
}

function getSelection(active: boolean, archived: boolean): ArchivedSelection {
    if (active && archived) {
        return "both";
    }
    if (active) {
        return "activeOnly";
    }
    if (archived) {
        return "archivedOnly";
    }
    return "none";
}

/** The backend status, or `undefined` when neither box is checked. */
function toArchivedStatus(
    selection: ArchivedSelection
): ArchivedStatus | undefined {
    switch (selection) {
        case "both":
            return "All";
        case "activeOnly":
            return "NotArchived";
        case "archivedOnly":
            return "Archived";
        case "none":
            return undefined;
        default:
            return assertNever(selection);
    }
}

/**
 * Derives the archived filter from the persisted `active`/`archived` data
 * params. The two booleans map onto the backend `ArchivedStatus`; setting either
 * box persists it (localStorage + URL) via the data-params API.
 */
export function getControlActivitiesArchivedFilter(
    dataParams: ControlActivitiesDataParamsApi
): ControlActivitiesArchivedFilterResult {
    const { active, archived } = dataParams.values;
    const selection = getSelection(active, archived);
    return {
        active,
        archived,
        setActive: (checked) =>
            dataParams.setValue(CONTROL_ACTIVITIES_DATA_PARAM.ACTIVE, checked),
        setArchived: (checked) =>
            dataParams.setValue(
                CONTROL_ACTIVITIES_DATA_PARAM.ARCHIVED,
                checked
            ),
        archivedStatus: toArchivedStatus(selection),
        isEnabled: selection !== "none",
    };
}
